import { useRef, useState } from 'react';
import type { EntryRow } from '../lib/day';
import { advanceTotal, dayApi } from '../lib/day';
import { openPrint } from '../lib/print';
import SignatureImage from './SignatureImage';
import InkPad, { type InkPadHandle } from './InkPad';
import { ReceiptSheets } from '../print/AdvanceReceipt';
import { formatDate, formatRs, rupeesInWords } from '../lib/money';

/**
 * "If the row has an advance, finalising opens a signature popup over the
 * screen: name, roles, amount in figures and words, date, an optional note, and
 * a white signature pad. He signs with his finger. Save is disabled until there
 * is ink; Clear wipes it."
 *
 * The pad itself — the drawing, and the PNG trimmed to the ink and compressed
 * on this device to stay under ~30KB — is InkPad, shared with the loan
 * receipt. The PNG is uploaded to R2 by key. It is never a data URL and never
 * touches the database — the prototype stores `toDataURL()` in its state blob,
 * which quietly blows the localStorage quota within weeks.
 */

interface Props {
  entry: EntryRow;
  workDate: string;
  onClose: () => void;
  onSaved: () => void;
}

export default function SignaturePad({ entry, workDate, onClose, onSaved }: Props) {
  const padRef = useRef<InkPadHandle | null>(null);
  const [hasInk, setHasInk] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [resigning, setResigning] = useState(false);
  /** The unsigned advance waiting on "Remove it" / "Keep". */
  const [removing, setRemoving] = useState<string | null>(null);

  const unsigned = entry.advances.filter((advance) => !advance.signed);
  const signed = entry.advances.filter((advance) => advance.signed);
  const total = advanceTotal(entry);
  const alreadySigned = unsigned.length === 0 && signed.length > 0 && !resigning;

  function clear() {
    padRef.current?.clear();
  }

  async function save() {
    setSaving(true);
    setError('');
    try {
      if (!padRef.current) throw new Error('There is no signature to save.');
      const png = await padRef.current.toPng();
      // Every unsigned advance for this man today is covered by the one
      // signature he gives for the total.
      for (const advance of unsigned) {
        await dayApi.saveSignature(workDate, advance.id, png);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the signature.');
    } finally {
      setSaving(false);
    }
  }

  /**
   * An advance entered on the wrong man, or the wrong amount, that he has not
   * signed for: no cash went out, so the office takes it back. Voided, never
   * deleted. A signed one is the owner's to cancel, from Approvals.
   */
  async function remove(id: string) {
    setSaving(true);
    setError('');
    try {
      await dayApi.voidAdvance(workDate, id, 'Entered by mistake — not signed for');
      setRemoving(null);
      onSaved();
      if (entry.advances.length === 1) onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove the advance.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-label="Advance received — signature">
      <div className="mbox">
        <div className="mhead">Advance received — signature</div>
        <div className="sub" style={{ marginBottom: 12 }}>
          Hand over the cash and have him sign on the tablet.
        </div>

        <div className="card" style={{ margin: '0 0 10px', background: 'var(--surface-2)' }}>
          <div className="row" style={{ paddingTop: 0 }}>
            <div>
              <div className="name">{entry.workerName}</div>
              <div className="sub">{entry.roleName ?? ''}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div className="name" style={{ fontSize: 22 }}>{formatRs(total)}</div>
              <div className="sub">{formatDate(workDate)}</div>
            </div>
          </div>
          <div className="sub" style={{ marginTop: 2 }}>
            Rupees {rupeesInWords(total)} only
          </div>
        </div>

        {alreadySigned ? (
          <>
            <div className="sub">
              Signed on{' '}
              {signed[0]?.signedAt
                ? new Date(signed[0].signedAt).toLocaleString('en-GB')
                : 'the tablet'}
            </div>
            {/* Fetched with the session token — an <img src> would come back
                401, because the browser does not send an Authorization header
                on an image request. */}
            <div className="sigdone">
              <SignatureImage advanceId={signed[0]!.id} />
            </div>
            <div className="bar" style={{ marginTop: 12 }}>
              <button type="button" className="btn" onClick={onClose}>
                Close
              </button>
              <button type="button" className="btn" onClick={() => setResigning(true)}>
                Sign again
              </button>
              <button
                type="button"
                className="btn pri"
                onClick={() => {
                  openPrint(
                    `Advance receipt — ${entry.workerName}`,
                    <ReceiptSheets
                      items={entry.advances.map((advance) => ({ entry, advance }))}
                      workDate={workDate}
                    />,
                  );
                  onClose();
                }}
              >
                Print receipt
              </button>
            </div>
          </>
        ) : (
          <>
            {unsigned.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <div className="sub">Not signed for yet — remove one entered by mistake:</div>
                {unsigned.map((advance) => (
                  <div
                    key={advance.id}
                    className="row"
                    style={{ padding: '6px 0', alignItems: 'center' }}
                  >
                    <div className="name">{formatRs(advance.amountRs)}</div>
                    {removing === advance.id ? (
                      <div className="bar">
                        <span className="sub">Remove {formatRs(advance.amountRs)}?</span>
                        <button
                          type="button"
                          className="btn sm"
                          disabled={saving}
                          onClick={() => setRemoving(null)}
                        >
                          Keep
                        </button>
                        <button
                          type="button"
                          className="btn sm dang"
                          disabled={saving}
                          onClick={() => void remove(advance.id)}
                        >
                          Remove it
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="btn sm dang"
                        disabled={saving}
                        onClick={() => setRemoving(advance.id)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="sub">Signature of {entry.workerName}</div>
            <InkPad ref={padRef} onInk={setHasInk} hasInk={hasInk} />

            {error && <div className="err">{error}</div>}

            <div className="bar" style={{ marginTop: 10 }}>
              <button type="button" className="btn" onClick={clear} disabled={saving}>
                Clear
              </button>
              {/* "Not now" leaves the row showing "no signature" in red. */}
              <button type="button" className="btn" onClick={onClose} disabled={saving}>
                Not now
              </button>
              <button
                type="button"
                className="btn pri"
                disabled={!hasInk || saving}
                onClick={() => void save()}
              >
                {saving ? 'Saving…' : 'Save signature'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
